// 画像最適化ユーティリティ
export interface ImageOptimizationOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'png' | 'webp';
}

/**
 * ファイルサイズに基づいて最適化設定を自動決定
 */
export const getAutoOptimizationSettings = (fileSize: number): ImageOptimizationOptions => {
  const sizeMB = fileSize / (1024 * 1024);
  
  if (sizeMB <= 0.5) {
    // 小さいファイル（500KB以下）: 高品質で保持
    return {
      maxWidth: 800,
      maxHeight: 600,
      quality: 0.9,
      format: 'jpeg'
    };
  } else if (sizeMB <= 1.0) {
    // 中程度のファイル（500KB-1MB）: 標準最適化
    return {
      maxWidth: 600,
      maxHeight: 450,
      quality: 0.8,
      format: 'jpeg'
    };
  } else if (sizeMB <= 2.0) {
    // 大きいファイル（1-2MB）: 積極的最適化
    return {
      maxWidth: 500,
      maxHeight: 375,
      quality: 0.7,
      format: 'jpeg'
    };
  } else {
    // 非常に大きいファイル（2MB以上）: 最大最適化
    return {
      maxWidth: 400,
      maxHeight: 300,
      quality: 0.6,
      format: 'jpeg'
    };
  }
};

/**
 * 画像を最適化してBase64文字列を返す
 */
export const optimizeImage = (
  file: File,
  options: ImageOptimizationOptions = {}
): Promise<string> => {
  // 自動最適化設定を取得
  const autoSettings = getAutoOptimizationSettings(file.size);
  
  const {
    maxWidth = autoSettings.maxWidth,
    maxHeight = autoSettings.maxHeight,
    quality = autoSettings.quality,
    format = autoSettings.format
  } = options;

  return new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context could not be created'));
        return;
      }
      
      const img = new Image();

      img.onload = () => {
        try {
          // アスペクト比を保ちながらサイズを調整
          let { width, height } = img;
          
          // 画像サイズの制限チェック
          if (width > 10000 || height > 10000) {
            reject(new Error('画像サイズが大きすぎます'));
            return;
          }
          
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          // 最小サイズの制限
          if (width < 1 || height < 1) {
            reject(new Error('画像サイズが小さすぎます'));
            return;
          }

          canvas.width = width;
          canvas.height = height;

          // 画像を描画
          ctx.drawImage(img, 0, 0, width, height);

          // 最適化された画像をBase64で取得
          const optimizedDataUrl = canvas.toDataURL(`image/${format}`, quality);
          
          // 結果の検証
          if (!optimizedDataUrl || optimizedDataUrl.length < 100) {
            reject(new Error('画像の最適化結果が無効です'));
            return;
          }
          
          resolve(optimizedDataUrl);
        } catch (error) {
          console.error('画像最適化処理エラー:', error);
          reject(new Error(`画像の最適化に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`));
        }
      };

      img.onerror = (error) => {
        console.error('画像読み込みエラー:', error);
        reject(new Error('画像の読み込みに失敗しました'));
      };

      // FileReaderでファイルを読み込み
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          img.src = e.target.result as string;
        } else {
          reject(new Error('ファイルの読み込み結果が無効です'));
        }
      };
      reader.onerror = (error) => {
        console.error('FileReader エラー:', error);
        reject(new Error('ファイルの読み込みに失敗しました'));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('画像最適化初期化エラー:', error);
      reject(new Error(`画像最適化の初期化に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`));
    }
  });
};

/**
 * 画像のサイズを確認
 */
export const getImageSize = (base64String: string): { width: number; height: number } => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      reject(new Error('画像サイズの取得に失敗しました'));
    };
    img.src = base64String;
  });
};

/**
 * Base64文字列のサイズを確認（KB単位）
 */
export const getBase64Size = (base64String: string): number => {
  const stringLength = base64String.length;
  const sizeInBytes = (stringLength * 3) / 4;
  return Math.round(sizeInBytes / 1024); // KB単位
};


